import type { ReactNode } from "react";
import type { NavIcon } from "../lib/nav";

export function Icon({ name }: { name: NavIcon }): ReactNode {
  const common = {
    width: 18,
    height: 18,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  switch (name) {
    case "dashboard":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="7" height="9" rx="1" />
          <rect x="14" y="3" width="7" height="5" rx="1" />
          <rect x="14" y="12" width="7" height="9" rx="1" />
          <rect x="3" y="16" width="7" height="5" rx="1" />
        </svg>
      );
    case "sessions":
      return (
        <svg {...common}>
          <rect x="2" y="4" width="20" height="14" rx="2" />
          <path d="M8 21h8M12 18v3" />
        </svg>
      );
    case "apiKeys":
      return (
        <svg {...common}>
          <circle cx="7.5" cy="15.5" r="4.5" />
          <path d="M10.7 12.3 20 3M15 8l3 3M18 5l3 3" />
        </svg>
      );
    case "properties":
      return (
        <svg {...common}>
          <path d="M3 11 12 3l9 8" />
          <path d="M5 10v10h14V10" />
          <path d="M10 20v-6h4v6" />
        </svg>
      );
    case "porteria":
      return (
        <svg {...common}>
          <path d="M4 21V5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v16" />
          <path d="M4 21h16M9 4v17M15 4v17M4 12h16" />
        </svg>
      );
    case "crmDashboard":
      return (
        <svg {...common}>
          <path d="M3 3v18h18" />
          <path d="M7 14l3-3 3 2 5-6" />
        </svg>
      );
    case "companies":
      return (
        <svg {...common}>
          <rect x="4" y="3" width="10" height="18" rx="1" />
          <path d="M14 8h5a1 1 0 0 1 1 1v12M7 7h4M7 11h4M7 15h4M17 12h1M17 16h1" />
        </svg>
      );
    case "contacts":
      return (
        <svg {...common}>
          <circle cx="9" cy="8" r="3.2" />
          <path d="M3.5 20a5.5 5.5 0 0 1 11 0" />
          <path d="M16 5.5a3 3 0 0 1 0 5.5M19.5 20a5 5 0 0 0-3-4.6" />
        </svg>
      );
    case "deals":
      return (
        <svg {...common}>
          <rect x="3" y="4" width="5" height="16" rx="1" />
          <rect x="10" y="4" width="5" height="10" rx="1" />
          <rect x="17" y="4" width="4" height="13" rx="1" />
        </svg>
      );
    case "pipelines":
      return (
        <svg {...common}>
          <path d="M4 6h16M4 12h10M4 18h6" />
          <circle cx="19" cy="12" r="1.6" />
          <circle cx="12" cy="18" r="1.6" />
        </svg>
      );
    case "supportDashboard":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case "tickets":
      return (
        <svg {...common}>
          <path d="M4 6h16v4a2 2 0 0 0 0 4v4H4v-4a2 2 0 0 0 0-4V6z" />
          <path d="M12 8v8" strokeDasharray="2 2" />
        </svg>
      );
    case "categories":
      return (
        <svg {...common}>
          <path d="M4 7h6l2 3h8v9H4z" />
          <path d="M4 7V5h6l2 2" />
        </svg>
      );
    case "sla":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4l3 3" />
          <path d="M8 4.5 6 3M16 4.5 18 3" />
        </svg>
      );
    case "billingDashboard":
      return (
        <svg {...common}>
          <rect x="3" y="3" width="18" height="18" rx="2" />
          <path d="M3 9h18M9 21V9" />
        </svg>
      );
    case "quotes":
      return (
        <svg {...common}>
          <path d="M14 3H6a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8z" />
          <path d="M14 3v5h5M9 13h6M9 17h4" />
        </svg>
      );
    case "invoices":
      return (
        <svg {...common}>
          <path d="M6 2h12v20l-3-2-3 2-3-2-3 2z" />
          <path d="M9 7h6M9 11h6M9 15h4" />
        </svg>
      );
    case "payments":
      return (
        <svg {...common}>
          <rect x="2" y="6" width="20" height="12" rx="2" />
          <circle cx="12" cy="12" r="2.6" />
          <path d="M6 10v4M18 10v4" />
        </svg>
      );
    case "accounts":
      return (
        <svg {...common}>
          <path d="M3 7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <path d="M3 10h18M16 14h2" />
        </svg>
      );
    case "products":
      return (
        <svg {...common}>
          <path d="M12 3 3 7.5 12 12l9-4.5z" />
          <path d="M3 7.5v9L12 21l9-4.5v-9M12 12v9" />
        </svg>
      );
    case "taxes":
      return (
        <svg {...common}>
          <path d="M4 5h16v4H4zM6 9v10M12 9v10M18 9v10M4 19h16" />
        </svg>
      );
  }
}
